import { DestroyRef, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import { GridApi } from 'ag-grid-community'

import { Observable, catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs'

import { MediaLibraryImage } from '@flowx/designer/modules/core/dictionary/media-library.dictionary'
import { PaginationQueryParams } from '@flowx/designer/shared/api/api-http.service'
import { PrivateDocumentsService } from '@flowx/designer/shared/services/documents-private.service'
import { GridSearchService } from '@flowx/designer/shared/services/grid-search.service'
import { MediaLibraryService } from '@flowx/designer/shared/services/media-library.service'
import {
  PaginatedEndpoint,
  PaginatedResponse,
  Sort,
} from '@flowx/designer/shared/services/paginated-datasource'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'

import { fetchStatusPropsByType } from './ai-document-intelligence.utils'

import { AiDocumentsApi } from '../../../../agents/documents/api/documents.api'
import { AiDocumentsFilenameService } from '../../../../agents/documents/services/ai-documents-filename.service'
import { AiDocumentsTransformService } from '../../../../agents/documents/services/ai-documents-transform.service'
import {
  AiAgent,
  AiDocumentContentType,
  AiDocumentStatus,
  AiDocumentSummary,
  UploadContextFile,
} from '../../../../common/ai.dictionary'
// eslint-disable-next-line import/no-cycle -- pre-existing structural cycle in new-ai module
import { AiEventsApi } from '../../../../common/api/ai-events.api'

const TABLE_LOAD_DELAY = 1000 // delay to allow the document to load in table and properly update status in real-time

Injectable({ providedIn: 'root' })
export class AiDocumentIntelligenceService extends GridSearchService<AiDocumentSummary> {
  #destroyRef = inject(DestroyRef)
  #route = inject(ActivatedRoute)
  #urlParamStore = inject(UrlParametersStoreService)
  #privateDocumentsService = inject(PrivateDocumentsService)
  #aiContextApi = inject(AiDocumentsApi)
  #aiDocumentsService = inject(AiDocumentsFilenameService)
  #mediaLibraryService = inject(MediaLibraryService)
  #aiEventsService = inject(AiEventsApi)
  #gridApi: GridApi = null

  constructor() {
    super()

    this.#aiEventsService.aiEvents$
      .pipe(
        filter(({ agent }) => agent === AiAgent.DOCUMENT_INTELLIGENCE),
        tap(({ unique_id, status }) => {
          const documentNode = this.#gridApi.getRowNode(unique_id)

          if (documentNode && documentNode?.data?.status !== status) {
            const statusTooltip = fetchStatusPropsByType(status as AiDocumentStatus)?.tooltip ?? ''

            documentNode.setData({ ...documentNode.data, status, statusTooltip })
          }
        }),
        filter(
          ({ status }) =>
            status &&
            [AiDocumentStatus.READY, AiDocumentStatus.EVALUATED].includes(
              status.toLowerCase() as AiDocumentStatus
            )
        ),
        switchMap(({ unique_id }) =>
          this.getDocumentById(this.#gridApi.getRowNode(unique_id).data)
        ),
        tap((document) => {
          const documentNode = this.#gridApi.getRowNode(document.id)
          documentNode.setDataValue('contents', document.contents)
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  setGridApi(gridApi: GridApi): void {
    this.#gridApi = gridApi
    super.setGridApi(gridApi)
  }

  getGridEndpointInfo(): {
    endpoint: PaginatedEndpoint<any, AiDocumentSummary>
    sort: Sort
    query: Partial<any>
  } {
    return {
      endpoint: (request, query) => this.getDocuments({ ...request, ...query }),
      sort: {
        property: 'modifiedDate',
        direction: 'desc',
      },
      query: { filename: this.#route.snapshot.queryParams?.searchQuery },
    }
  }

  getDocuments(params: PaginationQueryParams): Observable<PaginatedResponse<AiDocumentSummary[]>> {
    return this.#privateDocumentsService.getDocuments(params).pipe(
      map((response: any) => response.resourceContent || response),
      switchMap((privateDocuments) =>
        this.#aiContextApi
          .getDocumentsByAppId(this.#urlParamStore.urlParameters.appId)
          .pipe(map((contextDocuments) => [privateDocuments, contextDocuments]))
      ),
      map(([privateDocuments, contextDocuments]) => ({
        ...privateDocuments,
        content: privateDocuments.content.map(
          (privateDocument) =>
            this.#mapResource(privateDocument, contextDocuments) as AiDocumentSummary
        ),
      }))
    )
  }

  getDocumentById(document: AiDocumentSummary): Observable<AiDocumentSummary> {
    return this.#aiContextApi
      .getDocumentsByAppId(this.#urlParamStore.urlParameters.appId)
      .pipe(
        map((contextDocuments) =>
          this.#mapResource({ ...document, key: document.id }, contextDocuments)
        )
      )
  }

  uploadDocument(document: UploadContextFile): Observable<AiDocumentSummary> {
    let documentKey = null

    this.setLoading(true)
    return this.#aiDocumentsService.checkFilename(document.name).pipe(
      map((filename) =>
        AiDocumentsTransformService.mapDocumentsToMediaLibraryUploadPayload({
          ...document,
          name: filename,
          file: new File([document.file], filename),
        })
      ),
      tap((payload) => (documentKey = payload.key)),
      switchMap((payload) => this.#mediaLibraryService.addImage(payload, { suppressToasts: true })),
      tap((document) =>
        setTimeout(
          () => this.extractFromDocument(document, documentKey).subscribe(),
          TABLE_LOAD_DELAY
        )
      ),
      withLatestFrom(
        this.#aiContextApi.getDocumentsByAppId(this.#urlParamStore.urlParameters.appId)
      ),
      map(([uploadResponse, contextDocuments]) =>
        this.#mapResource(uploadResponse, contextDocuments)
      ),
      catchError((error) => {
        this.setLoading(false)
        throw error
      })
    )
  }

  extractFromDocument(
    document: MediaLibraryImage & { contentType: string },
    documentKey: string
  ): Observable<AiDocumentSummary> {
    const { appId, appVersionId, workspaceId } = this.#urlParamStore.urlParameters

    return this.#aiContextApi
      .extractFromDocument({
        unique_id: documentKey,
        object_name: document.storagePath,
        filename: document.filename,
        content_type: document.contentType,
        application_id: appId,
        application_version_id: appVersionId,
        workspace_id: workspaceId,
        resource_id: document.id,
        wait_for_summary: true,
        new_model: true,
      })
      .pipe(
        map(() => document),
        withLatestFrom(
          this.#aiContextApi.getDocumentsByAppId(this.#urlParamStore.urlParameters.appId)
        ),
        map(([uploadResponse, contextDocuments]) =>
          this.#mapResource(uploadResponse, contextDocuments)
        )
      )
  }

  deleteDocument(document: AiDocumentSummary): Observable<void> {
    if (document.diDocumentId) {
      return this.#aiContextApi
        .deleteDocumentById(document.diDocumentId)
        .pipe(switchMap(() => this.#mediaLibraryService.deleteImage(document.cmsDocument)))
    } else {
      return this.#mediaLibraryService.deleteImage(document.cmsDocument)
    }
  }

  #mapResource = (privateDocument, contextDocuments = []): AiDocumentSummary => {
    const matchingContextDocument = contextDocuments.find(
      ({ db_collection }) => db_collection === privateDocument.key
    )

    const { key, filename, modifiedBy, modifiedDate } = privateDocument
    const { details, status, id } = matchingContextDocument ?? {}

    const hasContentType = (type: AiDocumentContentType): boolean =>
      !!details?.content?.find((c) => c.type === type)

    const hasProcess = hasContentType(AiDocumentContentType.BUSINESS_PROCESS)
    const hasUiScreens = hasContentType(AiDocumentContentType.SCREEN_NAME)
    const hasBusinessRules = hasContentType(AiDocumentContentType.BUSINESS_RULE)
    const hasScreenExpression = hasContentType(AiDocumentContentType.SCREEN_EXPRESSION)

    const statusType = (status?.toLowerCase() as AiDocumentStatus) ?? AiDocumentStatus.NOT_PROCESSED

    return {
      id: key,
      filename,
      modifiedDate,
      modifiedBy,
      contents: {
        [AiDocumentContentType.BUSINESS_PROCESS]: hasProcess,
        [AiDocumentContentType.SCREEN_NAME]: hasUiScreens,
        [AiDocumentContentType.BUSINESS_RULE]: hasBusinessRules,
        [AiDocumentContentType.SCREEN_EXPRESSION]: hasScreenExpression,
      },
      contentType: [],
      status: statusType,
      statusTooltip: fetchStatusPropsByType(statusType)?.tooltip ?? '',
      cmsDocument: privateDocument.cmsDocument ?? privateDocument,
      diDocumentId: id,
    } satisfies AiDocumentSummary
  }
}
